/* @flow */

import type { NETGEM_API_PARENTAL_CONTROL } from '../../libs/netgemLibrary/v8/types/ParentalControl';
import type { KeyValuePair } from '../jsHelpers/types';

// Default values
export const DEFAULT_LOCK_PURCHASE = false;
export const DEFAULT_LOCKED_CHANNEL_IDS: Array<string> = [];
export const DEFAULT_MAX_AGE_ALLOWED_FOR_GAMES = 18;
export const DEFAULT_MAX_AGE_ALLOWED_FOR_VIDEO = 18;
export const DEFAULT_MINIMUM_AGE_FOR_ADULT_TV_CONTENT = 999; // Huge number to indicate that adult TV content is not restricted

// When parental control is paused (i.e. unlocked), it locks up again after 12h (in ms) or when the user closes the app
export const UNLOCK_DURATION: number = 43_200_000;

// When parental control is paused (i.e. unlocked), re-lock date is checked every 1 min (in ms)
export const LOCK_CHECK_INTERVAL = 60_000;

export type ParentalControlAndETag = { eTag: string | null, parentalControl: NETGEM_API_PARENTAL_CONTROL };

// PIN code is 4-digit long
export const PIN_LENGTH = 4;

export const PIN_SUCCESS_STATUS = 'Success';

export enum OtpResponseKind {
  CodeNotSet,
  Error,
  Success,
  OtpExpired,
  OtpNotSet,
  WrongOtp,
}

type ParentalControlItem = { label?: string, minAge: number };
export type ParentalControlExtendedItem = { ...ParentalControlItem, text: string };

type ParentalControlLabelsPerRatingSystem = Array<ParentalControlItem>;

export const ParentalControlLabels: KeyValuePair<ParentalControlLabelsPerRatingSystem> = {
  BBFC: [
    { label: 'U', minAge: 0 },
    { label: 'PG', minAge: 8 },
    { label: '12', minAge: 12 },
    { label: '15', minAge: 15 },
    { label: '18', minAge: 18 },
  ],
  default: [{ minAge: 0 }, { minAge: 10 }, { minAge: 12 }, { minAge: 16 }, { minAge: 18 }],
  IFCO: [
    { label: 'G', minAge: 0 },
    { label: 'PG', minAge: 10 },
    { label: '12', minAge: 12 },
    { label: '15', minAge: 15 },
    { label: '18', minAge: 18 },
  ],
  PEGI: [
    { label: 'PEGI 3', minAge: 3 },
    { label: 'PEGI 7', minAge: 7 },
    { label: 'PEGI 12', minAge: 12 },
    { label: 'PEGI 16', minAge: 16 },
    { label: 'PEGI 18', minAge: 18 },
  ],
  USA_PR: [
    { label: 'G, TV-Y', minAge: 0 },
    { label: 'TV-G', minAge: 2 },
    { label: 'TV-Y7', minAge: 7 },
    { label: 'TV-Y7-FV', minAge: 8 },
    { label: 'PG, TV-PG', minAge: 10 },
    { label: 'PG-13', minAge: 13 },
    { label: 'TV-14', minAge: 14 },
    { label: 'R, TV-MA', minAge: 17 },
    { label: 'NC-17', minAge: 18 },
  ],
};

export enum ParentalControlLockState {
  Locked,
  NotInitialized,
  Unlocked,
}

export enum ParentalControlLockReason {
  AdultContent,
  Channel,
  GameAge,
  Purchase,
  VideoAge,
}

export type ParentalControlItemState = {
  hasPinCode: boolean,
  lockReason?: ParentalControlLockReason,
  lockState: ParentalControlLockState,
};

export type ParentalControlPurchaseState = {
  hasPinCode: boolean,
  lockState: ParentalControlLockState,
};
